"use client";

import { useAuth } from "@clerk/nextjs";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { redeemTtvLaunchAccessAction } from "@/data/ttv/actions/redeemTtvLaunchAccessAction";
import { TTV_LAUNCH_AFFILIATE_STORAGE_KEY } from "@/features/ttv/launchAccess/constants";
import { getTtvLaunchAffiliate } from "@/features/ttv/launchAccess/getTtvLaunchAffiliate";
import { TtvLaunchAccessContext } from "@/features/ttv/launchAccess/TtvLaunchAccessContext";
import type {
  TtvLaunchAccessState,
  TtvLaunchAccessStatus,
} from "@/features/ttv/launchAccess/types";

const reportError = (message: string) =>
  toast.error(message, {
    id: "ttv-launch-access",
    action: { label: "Try again", onClick: () => window.location.reload() },
  });

const clearPendingAffiliate = () => {
  try {
    localStorage.removeItem(TTV_LAUNCH_AFFILIATE_STORAGE_KEY);
  } catch (storageError) {
    console.error("Failed to clear saved TTV affiliate access:", storageError);
  }
};

export function TtvLaunchAccessProvider({ children }: { children: ReactNode }) {
  const { userId } = useAuth();
  const [access, setAccess] = useState<TtvLaunchAccessState | null>(null);
  const requests = useRef(
    new Map<string, ReturnType<typeof redeemTtvLaunchAccessAction>>()
  );
  useEffect(() => {
    let isActive = true;
    const setStatus = (status: TtvLaunchAccessStatus) =>
      setAccess({ userId: userId ?? null, status });
    const captureAccess = async () => {
      const incoming = getTtvLaunchAffiliate(window.location.search);
      let pending: string | null = null;
      try {
        pending = getTtvLaunchAffiliate(
          localStorage.getItem(TTV_LAUNCH_AFFILIATE_STORAGE_KEY)
        );
        if (incoming !== null) {
          localStorage.setItem(TTV_LAUNCH_AFFILIATE_STORAGE_KEY, incoming);
        }
      } catch (storageError) {
        console.error("Failed to remember TTV affiliate access:", storageError);
      }
      const affiliateQuery = incoming ?? pending;
      if (userId === undefined) {
        return;
      }
      if (affiliateQuery === null || userId === null) {
        setStatus("idle");
        return;
      }
      setStatus("pending");
      try {
        const requestKey = `${userId}:${affiliateQuery}`;
        const request =
          requests.current.get(requestKey) ??
          redeemTtvLaunchAccessAction(affiliateQuery);
        requests.current.set(requestKey, request);
        const result = await request;
        if (!isActive) {
          return;
        }
        if (!result.success) {
          setStatus("error");
          reportError(result.error);
          return;
        }
        clearPendingAffiliate();
        // A fresh document updates persistent menus while keeping the visitor on the current page.
        if (result.hasNewAccess) {
          window.location.reload();
        } else {
          setStatus("granted");
        }
      } catch (requestError) {
        console.error("Failed to save TTV affiliate access:", requestError);
        if (isActive) {
          setStatus("error");
          reportError(
            "We couldn’t save your Text to vocals access. Please try again."
          );
        }
      }
    };
    void captureAccess();
    return () => {
      isActive = false;
    };
  }, [userId]);
  // Account changes invalidate the previous result during render, before the new account's effect runs.
  const currentAccess = access?.userId === userId ? access : null;
  return (
    <TtvLaunchAccessContext value={currentAccess}>
      {children}
    </TtvLaunchAccessContext>
  );
}
